import { useCallback, useEffect, useState } from "react";
import {
  Armchair,
  Copy,
  DoorOpen,
  Plus,
  Printer,
  QrCode,
  RefreshCw,
  RotateCw,
  Trash2,
  X,
} from "lucide-react";
import { api, ApiRequestError } from "../lib/api";
import type {
  OwnerOutlet,
  OwnerUnit,
  OwnerUnitsResponse,
  QrPayload,
} from "../lib/types";
import { PageHeader } from "../components/PageHeader";
import { RealtimeBadge } from "../components/RealtimeBadge";
import { formatDateTime } from "../lib/format";
import { useRealtime } from "../lib/realtime";

const statusLabel: Record<string, string> = {
  free: "Kosong",
  occupied: "Terisi",
  awaiting_payment: "Menunggu bayar",
  reserved: "Reservasi",
  cleaning: "Dibersihkan",
};

export function TablesPage() {
  const [data, setData] = useState<OwnerUnitsResponse | null>(null);
  const [outlets, setOutlets] = useState<OwnerOutlet[]>([]);
  const [outletId, setOutletId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [qr, setQr] = useState<{ unit: OwnerUnit; payload: QrPayload } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [type, setType] = useState<"table" | "room">("table");
  const [zone, setZone] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [units, outletResponse] = await Promise.all([
        api<OwnerUnitsResponse>(
          `/api/owner/units${outletId ? `?outletId=${encodeURIComponent(outletId)}` : ""}`,
        ),
        api<{ outlets: OwnerOutlet[] }>("/api/owner/outlets"),
      ]);
      setData(units);
      setOutlets(outletResponse.outlets);
      if (!outletId && outletResponse.outlets[0]) {
        setOutletId(outletResponse.outlets[0].id);
      }
      setError(null);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat memuat meja.",
      );
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    void load();
  }, [load]);

  const realtime = useRealtime({
    staff: true,
    onEvent: (event) => {
      if (
        [
          "order.created",
          "order.updated",
          "order.cancelled",
          "payment.paid",
          "payment.expired",
          "unit.updated",
        ].includes(event.type)
      ) {
        void load(true);
      }
    },
  });

  async function createUnit() {
    if (!label.trim()) return;
    setBusy("create");
    try {
      const suffix = outletId ? `?outletId=${encodeURIComponent(outletId)}` : "";
      await api(`/api/owner/units${suffix}`, {
        method: "POST",
        body: JSON.stringify({
          label: label.trim(),
          type,
          zone: zone.trim() || undefined,
        }),
      });
      setCreateOpen(false);
      setLabel("");
      setZone("");
      setType("table");
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal membuat meja.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function showQr(unit: OwnerUnit) {
    setBusy(`qr:${unit.id}`);
    try {
      const payload = await api<QrPayload>(`/api/owner/units/${unit.id}/qr`);
      setQr({ unit, payload });
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal memuat QR.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function rotateQr(unit: OwnerUnit) {
    if (
      !window.confirm(
        "Rotasi QR? QR lama tidak akan bisa dipakai lagi. Cetak ulang semua QR meja ini.",
      )
    ) {
      return;
    }
    setBusy(`rotate:${unit.id}`);
    try {
      const payload = await api<QrPayload>(
        `/api/owner/units/${unit.id}/qr/rotate`,
        { method: "POST" },
      );
      setQr({ unit, payload });
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal merotasi QR.",
      );
    } finally {
      setBusy(null);
    }
  }

  async function removeUnit(unit: OwnerUnit) {
    if (!window.confirm(`Hapus ${unit.label}? Tindakan ini tidak bisa dibatalkan.`)) {
      return;
    }
    setBusy(`delete:${unit.id}`);
    try {
      const suffix = outletId ? `?outletId=${encodeURIComponent(outletId)}` : "";
      await api(`/api/owner/units/${unit.id}${suffix}`, { method: "DELETE" });
      await load();
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Gagal menghapus meja.",
      );
    } finally {
      setBusy(null);
    }
  }

  function printQr(unit: OwnerUnit, payload: QrPayload) {
    const printWindow = window.open("", "_blank", "width=520,height=720");
    if (!printWindow) return;
    printWindow.document.write(`<!doctype html>
<html lang="id"><head><meta charset="utf-8"><title>QR ${unit.label}</title>
<style>
  body{font-family:system-ui,-apple-system,sans-serif;background:#fff;color:#211a14;margin:0;padding:28px;text-align:center}
  h1{font-size:22px;margin:8px 0 2px}
  p{margin:0;color:#6b5e52;font-size:13px}
  img{width:320px;height:320px;display:block;margin:22px auto}
  .label{font-size:28px;font-weight:800;margin-top:4px}
  .hint{margin-top:16px;font-size:12px;color:#6b5e52}
</style></head><body>
<p>${data?.outlet.name ?? ""}</p>
<h1>Scan untuk pesan</h1>
<div class="label">${unit.label}</div>
<img src="${payload.pngDataUrl}" alt="QR ${unit.label}">
<p class="hint">Buka kamera HP, scan QR, pilih menu, lalu bayar QRIS.</p>
<script>window.onload=function(){window.print()}</script>
</body></html>`);
    printWindow.document.close();
  }

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Meja & kamar"
        title="QR pemesanan"
        description="Setiap meja punya QR opaque. Cetak sekali, tempel di meja. Rotasi hanya jika QR hilang atau disalahgunakan."
        actions={
          <div className="kds-actions">
            <RealtimeBadge connected={realtime.connected} />
            <select
              className="input outlet-select"
              value={outletId}
              onChange={(event) => setOutletId(event.target.value)}
            >
              {outlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </select>
            <button type="button" className="btn btn-secondary" onClick={() => void load()}>
              <RefreshCw size={16} /> Muat ulang
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus size={16} /> Tambah
            </button>
          </div>
        }
      />

      {error ? <div className="alert alert--danger">{error}</div> : null}

      {loading ? (
        <div className="empty-state empty-state--roomy">
          <Armchair size={32} />
          <h3>Memuat meja...</h3>
        </div>
      ) : !data || data.units.length === 0 ? (
        <div className="empty-state empty-state--roomy">
          <Armchair size={36} />
          <h3>Belum ada meja</h3>
          <p>Tambahkan meja atau kamar, lalu cetak QR untuk pelanggan.</p>
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> Tambah meja pertama
          </button>
        </div>
      ) : (
        <div className="unit-grid">
          {data.units.map((unit) => (
            <article className="unit-card" key={unit.id}>
              <div className="unit-card__top">
                <span className={`unit-card__icon unit-card__icon--${unit.type}`}>
                  {unit.type === "room" ? <DoorOpen size={19} /> : <Armchair size={19} />}
                </span>
                <span className={`badge badge--${
                  unit.status === "free"
                    ? "success"
                    : unit.status === "awaiting_payment"
                      ? "warning"
                      : unit.status === "occupied"
                        ? "info"
                        : "neutral"
                }`}>
                  {statusLabel[unit.status] ?? unit.status}
                </span>
              </div>
              <h2>{unit.label}</h2>
              <p>{unit.zone ?? (unit.type === "room" ? "Kamar" : "Tanpa zona")}</p>
              <span className="unit-card__meta">
                QR {unit.qrRotatedAt ? `diperbarui ${formatDateTime(unit.qrRotatedAt)}` : "belum pernah dicetak"}
              </span>
              <div className="unit-card__actions">
                <button
                  type="button"
                  className="btn btn-primary btn--sm"
                  disabled={busy === `qr:${unit.id}`}
                  onClick={() => void showQr(unit)}
                >
                  <QrCode size={15} /> Lihat QR
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn--sm"
                  disabled={busy === `rotate:${unit.id}`}
                  onClick={() => void rotateQr(unit)}
                >
                  <RotateCw size={15} /> Rotasi
                </button>
                <button
                  type="button"
                  className="icon-button unit-card__delete"
                  disabled={busy === `delete:${unit.id}`}
                  onClick={() => void removeUnit(unit)}
                  title="Hapus meja"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {createOpen ? (
        <div className="sheet-backdrop" role="presentation" onClick={() => setCreateOpen(false)}>
          <div
            className="sheet sheet--form"
            role="dialog"
            aria-modal="true"
            aria-label="Tambah meja"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet__head">
              <div>
                <p className="eyebrow">Meja & kamar</p>
                <h2>Tambah unit</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setCreateOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="sheet__body">
              <div className="form-grid">
                <label className="field">
                  <span>Tipe</span>
                  <select
                    className="input"
                    value={type}
                    onChange={(event) => setType(event.target.value as "table" | "room")}
                  >
                    <option value="table">Meja</option>
                    <option value="room">Kamar</option>
                  </select>
                </label>
                <label className="field">
                  <span>Label</span>
                  <input
                    className="input"
                    autoFocus
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder={type === "room" ? "Kamar 01" : "Meja 09"}
                  />
                </label>
                <label className="field field--full">
                  <span>Zona (opsional)</span>
                  <input
                    className="input"
                    value={zone}
                    onChange={(event) => setZone(event.target.value)}
                    placeholder="Indoor / Outdoor / Lantai 2"
                  />
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setCreateOpen(false)}>
                  Batal
                </button>
                <button type="button" className="btn btn-primary" disabled={busy === "create"} onClick={() => void createUnit()}>
                  Simpan
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {qr ? (
        <div className="sheet-backdrop" role="presentation" onClick={() => setQr(null)}>
          <div
            className="sheet sheet--qr"
            role="dialog"
            aria-modal="true"
            aria-label={`QR ${qr.unit.label}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet__head">
              <div>
                <p className="eyebrow">{data?.outlet.name}</p>
                <h2>{qr.unit.label}</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setQr(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="sheet__body qr-sheet">
              <img className="qr-sheet__image" src={qr.payload.pngDataUrl} alt={`QR ${qr.unit.label}`} />
              <p className="fine-print">
                QR ini mengarah ke halaman pelanggan. Jangan tempel di tempat umum
                atau bagikan ke orang yang tidak berkepentingan.
              </p>
              <div className="qr-sheet__url">
                <code>{qr.payload.url}</code>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => void navigator.clipboard.writeText(qr.payload.url)}
                  title="Salin URL"
                >
                  <Copy size={15} />
                </button>
              </div>
            </div>
            <div className="sheet__foot qr-sheet__actions">
              <button type="button" className="btn btn-secondary" onClick={() => void navigator.clipboard.writeText(qr.payload.url)}>
                <Copy size={16} /> Salin tautan
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => printQr(qr.unit, qr.payload)}>
                <Printer size={16} /> Cetak
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={busy === `rotate:${qr.unit.id}`}
                onClick={() => void rotateQr(qr.unit)}
              >
                <RotateCw size={16} /> Rotasi QR
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
